import { useTranslation } from 'react-i18next'
import { AlertCircle, ExternalLink, Loader2, Plus } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { processingNetworkShortLabel } from '@/lib/processing-network'
import { useTagadapayAccounts, type TagadapayAccount } from '@/lib/api/processors'
import { getTagadaDashboardUrl, getTagadaTpaDashboardUrl } from '@/lib/tagadapay-dashboard'
import { useTagadapayApply } from '@/hooks/use-tagadapay-apply'
import { tpaStatusMeta } from '@/lib/tagadapay-tpa-status'

// The underlying acquirer (Adyen/Stripe/…) must never be exposed to the
// merchant — accounts are labelled by their neutral, numbered Processing
// Network instead (the same identity printed on their signed contract).
function accountTitle(account: Pick<TagadapayAccount, 'payfacProvider'>): string {
    return processingNetworkShortLabel(account.payfacProvider)
}

interface TagadaPayAccountFieldProps {
    value: string
    onChange: (accountId: string) => void
    disabled?: boolean
}

export const TagadaPayAccountField = ({ value, onChange, disabled }: TagadaPayAccountFieldProps) => {
    const { t } = useTranslation('processors')
    const { data: accounts, isLoading, isError, refetch } = useTagadapayAccounts()
    const apply = useTagadapayApply()

    const selectedDashboardUrl = value ? getTagadaTpaDashboardUrl(value) : apply.href

    if (isLoading) {
        return (
            <div>
                <Label>{t('tagadapayRouter.accountLabel')}</Label>
                <div className="text-muted-foreground mt-2 flex items-center gap-2 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('tagadapayRouter.accountLoading')}
                </div>
            </div>
        )
    }

    if (isError) {
        return (
            <div>
                <Label>{t('tagadapayRouter.accountLabel')}</Label>
                <div className="bg-destructive/10 text-destructive mt-2 flex items-center justify-between gap-3 rounded-md p-3 text-sm">
                    <span className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        {t('tagadapayRouter.accountErrorTitle')}
                    </span>
                    <Button type="button" variant="outline" size="sm" onClick={() => refetch()}>
                        {t('tagadapayRouter.accountRetry')}
                    </Button>
                </div>
            </div>
        )
    }

    const list = accounts ?? []

    // Surface a previously-saved account id even if it's no longer returned
    // by the list (different env, archived, etc.) so we never silently drop a
    // working configuration.
    const hasCurrent = !value || list.some((a) => a.id === value)

    if (list.length === 0 && !value) {
        return (
            <div>
                <Label>{t('tagadapayRouter.accountLabel')}</Label>
                <div className="border-border bg-muted/30 mt-2 flex flex-col items-start gap-3 rounded-lg border border-dashed p-5">
                    <div>
                        <p className="text-foreground text-sm font-medium">
                            {t(`tagadapayApply.${apply.intent}Title`)}
                        </p>
                        <p className="text-muted-foreground mt-1 text-sm">
                            {t(`tagadapayApply.${apply.intent}Body`)}
                        </p>
                    </div>
                    <Button type="button" asChild>
                        <a href={apply.href} target="_blank" rel="noopener noreferrer">
                            {apply.canApply && <Plus className="mr-2 h-4 w-4" />}
                            {t(`tagadapayApply.${apply.intent}Cta`)}
                        </a>
                    </Button>
                </div>
            </div>
        )
    }

    const selected = list.find((a) => a.id === value)
    const selectedInactive = !!selected && selected.status !== 'active'

    return (
        <div>
            <Label htmlFor="tagadapayAccountId">{t('tagadapayRouter.accountLabel')}</Label>
            <Select value={value} onValueChange={onChange} disabled={disabled}>
                <SelectTrigger id="tagadapayAccountId" className="mt-1.5">
                    <SelectValue placeholder={t('tagadapayRouter.accountSelectPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                    {!hasCurrent && value && (
                        <SelectItem value={value}>
                            <span className="font-mono text-xs">{value}</span>
                        </SelectItem>
                    )}
                    {list.map((account) => {
                        const meta = tpaStatusMeta(account.status)
                        return (
                            <SelectItem key={account.id} value={account.id}>
                                <span className="flex items-center gap-2">
                                    <span className="font-medium">{accountTitle(account)}</span>
                                    {account.mode === 'test' && (
                                        <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                                            {t('tagadapayRouter.accountTestBadge')}
                                        </Badge>
                                    )}
                                    <Badge variant={meta.variant} className="px-1.5 py-0 text-[10px]">
                                        {t(meta.labelKey)}
                                    </Badge>
                                    <span className="text-muted-foreground font-mono text-xs">
                                        {account.id}
                                    </span>
                                </span>
                            </SelectItem>
                        )
                    })}
                </SelectContent>
            </Select>

            <p
                className={cn(
                    'mt-1.5 text-sm',
                    selectedInactive ? 'text-amber-600 dark:text-amber-500' : 'text-muted-foreground',
                )}
            >
                {selectedInactive
                    ? t('tagadapayRouter.accountInactiveHint')
                    : t('tagadapayRouter.accountSelectHint')}
            </p>

            {selected ? (
                <a
                    href={selectedDashboardUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground mt-2 inline-flex items-center gap-1.5 text-sm underline-offset-4 transition hover:underline"
                >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {t('tagadapayRouter.openDashboard')}
                </a>
            ) : (
                <a
                    href={getTagadaDashboardUrl('/merchant/requests/new', { from: 'crm' })}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground mt-2 inline-flex items-center gap-1.5 text-sm underline-offset-4 transition hover:underline"
                >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {t('tagadapayRouter.accountCreateAnother')}
                </a>
            )}
        </div>
    )
}
